import { Link } from 'react-router-dom';
import { pdfDownloadHref } from '@/lib/book';
import type { ViewMode } from '@/pages/reader/readerUtils';

interface ReaderToolbarProps {
  page: number;
  viewMode: ViewMode;
  zoom: number;
  flipEnabled: boolean;
  isFullscreen: boolean;
  onOpenToc: () => void;
  onToggleView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onToggleFlip: () => void;
  onToggleFullscreen: () => void;
}

const iconBtn =
  'flex h-11 w-11 items-center justify-center rounded-md text-background-50 transition-colors hover:bg-primary-800';

export default function ReaderToolbar({
  page,
  viewMode,
  zoom,
  flipEnabled,
  isFullscreen,
  onOpenToc,
  onToggleView,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onToggleFlip,
  onToggleFullscreen,
}: ReaderToolbarProps) {
  return (
    <header className="z-30 w-full border-b border-primary-800/70 bg-primary-950">
      <div className="flex h-14 w-full items-center justify-between gap-2 px-2 md:h-16 md:px-4">
        <div className="flex min-w-0 items-center gap-1">
          <Link to="/" aria-label="표지로 돌아가기" className={iconBtn}>
            <i className="ri-home-4-line text-xl" aria-hidden="true"></i>
          </Link>
          <button type="button" onClick={onOpenToc} aria-label="차례 열기" className={iconBtn}>
            <i className="ri-list-unordered text-xl" aria-hidden="true"></i>
          </button>
          <div className="ml-1 hidden min-w-0 flex-col leading-tight sm:flex">
            <span className="truncate font-heading text-base font-bold text-background-50">봉사의 길</span>
            <span className="truncate font-label text-xs text-accent-300">세창 박종윤 사진 자서전</span>
          </div>
        </div>

        <div className="flex items-center gap-0.5 md:gap-1">
          <button
            type="button"
            onClick={onToggleView}
            aria-label={viewMode === 'spread' ? '한 쪽 보기로 전환' : '두 쪽 보기로 전환'}
            className={`${iconBtn} w-auto gap-1 px-3`}
          >
            <i className={viewMode === 'spread' ? 'ri-file-copy-2-line text-lg' : 'ri-file-copy-line text-lg'} aria-hidden="true"></i>
            <span className="hidden font-label text-sm lg:inline">
              {viewMode === 'spread' ? '두 쪽' : '한 쪽'}
            </span>
          </button>

          <button type="button" onClick={onZoomOut} aria-label="축소" className={iconBtn} disabled={zoom <= 1}>
            <i className="ri-zoom-out-line text-xl" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={onResetZoom}
            aria-label="배율 초기화"
            className="hidden h-11 min-w-[52px] items-center justify-center rounded-md font-label text-sm text-accent-300 transition-colors hover:bg-primary-800 md:flex"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" onClick={onZoomIn} aria-label="확대" className={iconBtn} disabled={zoom >= 3}>
            <i className="ri-zoom-in-line text-xl" aria-hidden="true"></i>
          </button>

          <button
            type="button"
            onClick={onToggleFlip}
            aria-label={flipEnabled ? '책장 넘김 효과 끄기' : '책장 넘김 효과 켜기'}
            aria-pressed={flipEnabled}
            className={`${iconBtn} ${flipEnabled ? 'bg-primary-700 text-accent-300' : ''}`}
          >
            <i className="ri-book-2-line text-xl" aria-hidden="true"></i>
          </button>

          <a href={pdfDownloadHref()} download aria-label="PDF 내려받기" className={iconBtn}>
            <i className="ri-download-2-line text-xl" aria-hidden="true"></i>
          </a>

          <button type="button" onClick={onToggleFullscreen} aria-label="전체화면 켜기/끄기" className={iconBtn}>
            <i className={isFullscreen ? 'ri-fullscreen-exit-line text-xl' : 'ri-fullscreen-line text-xl'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <span className="sr-only" aria-live="polite">
        현재 {page}쪽
      </span>
    </header>
  );
}